// Unit 06 — IPv4 Addressing (Configuration Lab: BUILD → BREAK → FIX → VERIFY)
import { observe, note, readConfirm } from '../../ui/widgets.js';
import { branch, branchLayout } from './topologies.js';
import { ran, pinged, resolved, reach, webWorks, hostCfg } from './helpers.js';
import { apipaFor } from '../../engine/net/sim.js';

function network() {
  const n = branch();
  const PC2 = n.devices.PC2;
  // Fresh out of the box: DHCP client, no DHCP server on this LAN → APIPA.
  PC2.ipcfg = { dhcp: true, ip: null, mask: null, gw: null, dns: [], ipv6: [] };
  PC2.apipa = apipaFor(PC2.nic.mac);
  return n;
}
const layout = {
  ...branchLayout,
  nodes: { ...branchLayout.nodes, PC2: { ...branchLayout.nodes.PC2, sub: 'NEW · finance · not configured' } },
};

export default {
  id: 'u06', num: '06', title: 'IPv4 Addressing', type: 'Configuration Lab', implemented: true,
  objective: 'Read and apply an IPv4 addressing plan: host address, mask, default gateway and DNS — and fix an endpoint that has them wrong.',
  network, layout, adapterGui: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Anatomy of an IPv4 configuration', workspace: false,
      content: `
<p>An IPv4 address is 32 bits, written as four octets: <code>192.168.10.25</code>. The <b>subnet mask</b> says which bits are the network and which are the host.</p>
<pre>192.168.10.25   = 11000000.10101000.00001010.00011001
255.255.255.0   = 11111111.11111111.11111111.00000000   (/24)
                  |------- network (24) -------|-host-|
Network 192.168.10.0 · Broadcast 192.168.10.255 · Hosts .1 – .254</pre>
<table>
<tr><th>Setting</th><th>What it does</th><th>If it is wrong</th></tr>
<tr><td>IP address</td><td>Identifies this host</td><td>Duplicates / wrong subnet → nothing local works</td></tr>
<tr><td>Subnet mask</td><td>Decides “local or remote?”</td><td>Host sends to gateway when it should not (or vice versa)</td></tr>
<tr><td>Default gateway</td><td>Where every <i>remote</i> packet goes</td><td>Local works, everything remote fails</td></tr>
<tr><td>DNS server</td><td>Turns names into addresses</td><td>IPs work, names fail</td></tr>
</table>
<h3>Special ranges</h3>
<ul>
<li><b>Private (RFC 1918)</b>: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16 — need NAT to reach the internet.</li>
<li><b>169.254.0.0/16 (APIPA)</b>: Windows gave itself an address because DHCP failed. A symptom, never a configuration.</li>
<li><b>127.0.0.0/8</b>: loopback (this host). <b>0.0.0.0/0</b>: “any” / default route.</li>
</ul>`,
      widgets: [
        observe({
          id: 'u06-calc', title: 'WORK IT OUT', intro: 'PC1 is 192.168.10.10 with mask 255.255.255.0.',
          fields: [
            { label: 'Network address', check: (v) => v === '192.168.10.0' || 'Keep the network bits, set host bits to 0.' },
            { label: 'Broadcast address', check: (v) => v === '192.168.10.255' || 'Keep the network bits, set host bits to 1.' },
            { label: 'Number of usable host addresses', check: (v) => v === '254' || '8 host bits → 2⁸ addresses, minus network and broadcast.' },
          ],
        }),
      ],
      question: 'Why can a PC with the right IP and mask but the wrong gateway still talk to its neighbours?',
    },
    {
      id: 'configure', phase: 'CONFIGURE', title: 'Address the new finance PC', devices: ['PC2'],
      ticket: { id: '1006', priority: 4, from: 'IT Asset Management', subject: 'New finance PC2 at desk 15 — static address required', body: 'Finance software licensing is tied to the IP, so PC2 must be static. Please configure it from the BRANCH-01 addressing plan and confirm internet access.' },
      content: `
<table>
<tr><th colspan="2">BRANCH-01 addressing plan · 192.168.10.0/24</th></tr>
<tr><td>.1</td><td>Default gateway (R1 Gi0/0)</td></tr>
<tr><td>.2 – .20</td><td>Network infrastructure</td></tr>
<tr><td>.21 – .99</td><td>Static hosts — <b>PC2 is assigned .25</b></td></tr>
<tr><td>.100 – .200</td><td>Reserved for DHCP (future)</td></tr>
<tr><td>DNS</td><td>8.8.8.8</td></tr>
</table>
<p>Check PC2 first, then configure it — either with the <b>⚙ adapter settings</b> button under the diagram, or from PowerShell:</p>
<pre>netsh interface ip set address name="Ethernet" static &lt;ip&gt; &lt;mask&gt; &lt;gateway&gt;
netsh interface ip set dns name="Ethernet" static &lt;dns&gt;</pre>`,
      evidence: [
        { id: 'before', text: 'PC2: initial (unconfigured) state captured', match: (ev) => ran('PC2', ['ipconfig', 'ipconfig /all'])(ev) && /169\.254\./.test(ev.output) },
        { id: 'after', text: 'PC2: new configuration displayed', match: (ev) => ran('PC2', ['ipconfig', 'ipconfig /all'])(ev) && /192\.168\.10\.25/.test(ev.output) },
        { id: 'gw', text: 'PC2: gateway answers', match: pinged('PC2', '192.168.10.1', { success: true }) },
        { id: 'dns', text: 'PC2: DNS resolves an internet name', match: (ev) => resolved('PC2', null)(ev) },
      ],
      tasks: [
        { id: 'plan', text: 'PC2 is statically configured as 192.168.10.25/24, gw .1, DNS 8.8.8.8', check: (rt) => { const d = rt.net.devices.PC2.ipcfg; return !d.dhcp && d.ip === '192.168.10.25' && d.mask === '255.255.255.0' && d.gw === '192.168.10.1' && d.dns[0] === '8.8.8.8'; }, fail: 'PC2’s configuration does not match the plan.' },
        { id: 'web', text: 'PC2 can browse www.example.com', check: (rt) => webWorks(rt, 'PC2'), fail: 'PC2 cannot reach the web by name.' },
      ],
      question: 'PC2 first shows a 169.254.x.x address. What does that prove about how it was configured?',
      hints: ['Run <code>ipconfig</code> before changing anything, so you have a “before” picture.', 'With netsh, the gateway is the third value after <code>static</code>. The DNS server is set with a separate command.'],
    },
    {
      id: 'break', phase: 'BREAK → FIX', title: 'Re-imaged reception PC', devices: ['PC1', 'PC2'],
      onEnter: (rt) => { rt.net.devices.PC1.ipcfg.gw = '192.168.10.100'; rt.net.devices.PC1.arp = {}; },
      ticket: { id: '1006-2', priority: 3, from: 'Karen (Reception)', subject: 'Reception PC: shared files OK, no websites', body: 'An intern re-imaged my PC this morning and set the network up by hand. I can open files on the finance PC but no website loads and Teams is offline.' },
      content: '<p>Local works, remote fails. Collect the evidence that explains exactly that combination, then fix PC1 against the addressing plan.</p>',
      evidence: [
        { id: 'local', text: 'PC1: local host (PC2) reachable', match: pinged('PC1', '192.168.10.25', { success: true }) },
        { id: 'remote', text: 'PC1: a remote destination tested', match: (ev) => (pinged('PC1', null)(ev) && ev.args?.ip && !ev.args.ip.startsWith('192.168.10.')) || (ev.device === 'PC1' && ['curl', 'tracert', 'Test-NetConnection', 'nslookup'].includes(ev.key)) },
        { id: 'cfg', text: 'PC1: IP configuration captured', match: ran('PC1', ['ipconfig', 'ipconfig /all', 'route print', 'netsh show config']) },
      ],
      tasks: [
        { id: 'gw', text: 'PC1 uses the documented default gateway', check: (rt) => hostCfg(rt, 'PC1')?.gw === '192.168.10.1', fail: 'PC1’s gateway still differs from the plan.' },
        { id: 'web', text: 'PC1 can browse www.example.com', check: (rt) => webWorks(rt, 'PC1'), fail: 'PC1 still cannot browse.' },
        { id: 'keep', text: 'PC1 keeps its assigned address 192.168.10.10/24', check: (rt) => hostCfg(rt, 'PC1')?.ip === '192.168.10.10' && hostCfg(rt, 'PC1')?.mask === '255.255.255.0', fail: 'PC1’s address or mask was changed — only fix what is wrong.' },
      ],
      question: 'The ping to 8.8.8.8 says “Reply from 192.168.10.10: Destination host unreachable” — a reply from PC1 itself. What was PC1 trying to reach?',
      hints: ['Compare every line of <code>ipconfig</code> with the addressing plan.', 'Windows reports “Destination host unreachable” from its own IP when ARP for the next hop gets no answer — here, the next hop is the gateway it was given.'],
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'Prove it end to end', devices: ['PC1', 'PC2'],
      content: '<p>Show that PC1 reaches the gateway, the internet by IP, and the internet by name.</p>',
      evidence: [
        { id: 'gw', text: 'PC1: gateway 192.168.10.1 answers', match: pinged('PC1', '192.168.10.1', { success: true }) },
        { id: 'ip', text: 'PC1: 8.8.8.8 answers', match: pinged('PC1', '8.8.8.8', { success: true }) },
        { id: 'name', text: 'PC1: a website loads or resolves by name', match: (ev) => resolved('PC1', null)(ev) || (ev.device === 'PC1' && ev.key === 'curl' && ev.result?.success) || (pinged('PC1', 'www.example.com', { success: true })(ev)) },
      ],
      widgets: [note({ id: 'u06-close', title: 'TICKET CLOSURE NOTE', prompt: 'What was misconfigured on PC1, why did local traffic still work, and what did you change?', min: 40 })],
      verifyLabel: 'Verify & complete unit',
    },
  ],
  review: () => '<p>The subnet mask decides “local or remote”; the gateway only matters for remote traffic. That is why a wrong gateway produces the classic <b>“local works, internet doesn’t”</b> symptom.</p>',
};
